import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowRight, 
  Clock, 
  DollarSign,
  TrendingUp,
  MapPin,
  Briefcase,
  Search,
  Building2
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';

export default function JobSeekersPage() {
  const [applyForm, setApplyForm] = useState({
    name: '',
    email: '',
    phone: '',
    jobTitle: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Mock submission
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    toast.success('Application submitted!', {
      description: 'We will review your application and get back to you soon.'
    });
    
    setApplyForm({
      name: '',
      email: '',
      phone: '',
      jobTitle: '',
      message: ''
    });
    setSelectedJob(null);
    setIsSubmitting(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setApplyForm(prev => ({ ...prev, [name]: value }));
  };

  const categories = [
    { name: 'General Labour', count: '25+ jobs', icon: Briefcase },
    { name: 'Forklift Operators', count: '18+ jobs', icon: Building2 },
    { name: 'Warehouse Associates', count: '30+ jobs', icon: Building2 },
    { name: 'Office Roles', count: '15+ jobs', icon: Briefcase }
  ];

  const benefits = [
    {
      title: 'Weekly Pay',
      description: 'Get paid every week, on time, every time.',
      icon: DollarSign
    },
    {
      title: 'Flexible Schedules',
      description: 'Choose shifts that fit your lifestyle and commitments.',
      icon: Clock
    },
    {
      title: 'Career Growth',
      description: 'Access training and opportunities for advancement.',
      icon: TrendingUp
    }
  ];

  const jobs = [
    {
      id: 1,
      title: 'Forklift Operator',
      company: 'Metro Distribution',
      location: 'Brampton, ON',
      type: 'Full-time',
      pay: '$20-24/hr',
      posted: '2 days ago',
      description: 'Experienced forklift operator needed for busy warehouse. Must have valid certification and minimum 1 year experience.'
    },
    {
      id: 2,
      title: 'Warehouse Associate',
      company: 'Global Freight Solutions',
      location: 'Mississauga, ON',
      type: 'Full-time',
      pay: '$17-19/hr',
      posted: '1 day ago',
      description: 'Looking for reliable warehouse associates for picking, packing, and shipping duties. Physical job requiring lifting up to 50lbs.'
    },
    {
      id: 3,
      title: 'General Labourer',
      company: 'BuildCorp Manufacturing',
      location: 'Milton, ON',
      type: 'Contract',
      pay: '$18-20/hr',
      posted: '3 days ago',
      description: 'Manufacturing facility seeking general labourers for production line work. Training provided for the right candidates.'
    },
    {
      id: 4,
      title: 'Administrative Assistant',
      company: 'TechLogix Inc.',
      location: 'Toronto, ON',
      type: 'Temp-to-Perm',
      pay: '$22-26/hr',
      posted: '4 days ago',
      description: 'Office support role handling scheduling, correspondence, and general administrative duties. MS Office proficiency required.'
    },
    {
      id: 5,
      title: 'Shipping & Receiving Clerk',
      company: 'Prime Logistics',
      location: 'Vaughan, ON',
      type: 'Full-time',
      pay: '$19-22/hr',
      posted: '1 day ago',
      description: 'Manage incoming and outgoing shipments, verify documentation, and maintain inventory records. Detail-oriented individuals preferred.'
    },
    {
      id: 6,
      title: 'Machine Operator',
      company: 'Apex Industries',
      location: 'Cambridge, ON',
      type: 'Full-time',
      pay: '$21-25/hr',
      posted: '5 days ago',
      description: 'Operate and monitor machinery in a manufacturing environment. Previous machine operation experience is an asset.'
    }
  ];

  const filteredJobs = jobs.filter(job => 
    job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    job.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
    job.location.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <main data-testid="job-seekers-page">
      {/* Hero Section */}
      <section 
        className="page-header relative"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/4487382/pexels-photo-4487382.jpeg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
        data-testid="job-seekers-hero"
      >
        <div className="absolute inset-0 bg-[#0B1F3A]/90" />
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl"
          >
            <span className="inline-block px-4 py-2 bg-white/10 rounded-full text-white/90 text-sm font-medium mb-6">
              For Job Seekers
            </span>
            <h1 className="font-['Chivo'] font-black text-4xl md:text-5xl lg:text-6xl text-white leading-tight tracking-tight mb-6">
              Find Your Next Opportunity
            </h1>
            <p className="text-lg md:text-xl text-white/80 leading-relaxed">
              Discover exciting job opportunities across the Greater Toronto Area. We connect talented workers with top employers.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Job Categories */}
      <section className="section-padding bg-white" data-testid="job-categories">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <span className="text-sm text-[#64748B] uppercase tracking-[0.2em] font-bold">
              Job Categories
            </span>
            <h2 className="font-['Chivo'] font-black text-3xl md:text-4xl text-[#0B1F3A] mt-3 tracking-tight">
              Explore Opportunities
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {categories.map((category, index) => (
              <motion.div
                key={category.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="border border-[#E2E8F0] p-6 hover:border-[#1E90FF] hover:bg-[#F5F7FA] transition-colors cursor-pointer group"
              >
                <div className="w-12 h-12 bg-[#F5F7FA] group-hover:bg-white rounded-lg flex items-center justify-center mb-4 transition-colors">
                  <category.icon size={24} className="text-[#1E90FF]" strokeWidth={1.5} />
                </div>
                <h3 className="font-['Chivo'] font-bold text-lg text-[#0B1F3A] mb-1">
                  {category.name}
                </h3>
                <p className="text-sm text-[#64748B]">{category.count}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Work With Us */}
      <section className="section-padding bg-[#F5F7FA]" data-testid="why-work-with-us">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <span className="text-sm text-[#64748B] uppercase tracking-[0.2em] font-bold">
              Benefits
            </span>
            <h2 className="font-['Chivo'] font-black text-3xl md:text-4xl text-[#0B1F3A] mt-3 tracking-tight">
              Why Work With FleetHire
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white border border-[#E2E8F0] p-8"
              >
                <div className="w-14 h-14 bg-[#F5F7FA] rounded-lg flex items-center justify-center mb-6">
                  <benefit.icon size={28} className="text-[#FF6B00]" strokeWidth={1.5} />
                </div>
                <h3 className="font-['Chivo'] font-bold text-xl text-[#0B1F3A] mb-3">
                  {benefit.title}
                </h3>
                <p className="text-[#64748B] leading-relaxed">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Job Listings */}
      <section className="section-padding bg-white" data-testid="job-listings">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="mb-8"
          >
            <span className="text-sm text-[#64748B] uppercase tracking-[0.2em] font-bold">
              Current Openings
            </span>
            <h2 className="font-['Chivo'] font-black text-3xl md:text-4xl text-[#0B1F3A] mt-3 tracking-tight">
              Available Positions
            </h2>
          </motion.div>

          {/* Search Bar */}
          <div className="mb-8">
            <div className="relative max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#64748B]" size={20} />
              <Input
                type="text"
                placeholder="Search jobs by title, company, or location..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 py-3"
                data-testid="job-search-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {filteredJobs.map((job, index) => (
              <motion.div
                key={job.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                viewport={{ once: true }}
                className="job-card"
                data-testid={`job-card-${job.id}`}
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="font-['Chivo'] font-bold text-lg text-[#0B1F3A] mb-1">
                      {job.title}
                    </h3>
                    <p className="text-[#64748B] text-sm">{job.company}</p>
                  </div>
                  <span className="bg-[#F5F7FA] text-[#0B1F3A] text-xs font-medium px-3 py-1 rounded-full">
                    {job.type}
                  </span>
                </div>

                <div className="flex flex-wrap gap-4 mb-4 text-sm text-[#64748B]">
                  <span className="flex items-center gap-1">
                    <MapPin size={14} strokeWidth={1.5} />
                    {job.location}
                  </span>
                  <span className="flex items-center gap-1">
                    <DollarSign size={14} strokeWidth={1.5} />
                    {job.pay}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock size={14} strokeWidth={1.5} />
                    {job.posted}
                  </span>
                </div>

                <p className="text-[#64748B] text-sm mb-4 line-clamp-2">
                  {job.description}
                </p>

                <Dialog>
                  <DialogTrigger asChild>
                    <button
                      onClick={() => {
                        setSelectedJob(job);
                        setApplyForm(prev => ({ ...prev, jobTitle: job.title }));
                      }}
                      className="btn-primary btn-hover-arrow text-sm px-6 py-2"
                      data-testid={`apply-btn-${job.id}`}
                    >
                      Quick Apply
                      <ArrowRight size={14} className="arrow-icon" strokeWidth={2} />
                    </button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[500px]" data-testid="apply-dialog">
                    <DialogHeader>
                      <DialogTitle className="font-['Chivo'] font-bold text-xl">
                        Apply for {selectedJob?.title}
                      </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-[#0B1F3A] mb-2">
                          Full Name *
                        </label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          value={applyForm.name}
                          onChange={handleChange}
                          required
                          placeholder="Your full name"
                          data-testid="apply-input-name"
                        />
                      </div>
                      <div>
                        <label htmlFor="applyEmail" className="block text-sm font-medium text-[#0B1F3A] mb-2">
                          Email *
                        </label>
                        <Input
                          id="applyEmail"
                          name="email"
                          type="email"
                          value={applyForm.email}
                          onChange={handleChange}
                          required
                          placeholder="your@email.com"
                          data-testid="apply-input-email"
                        />
                      </div>
                      <div>
                        <label htmlFor="applyPhone" className="block text-sm font-medium text-[#0B1F3A] mb-2">
                          Phone *
                        </label>
                        <Input
                          id="applyPhone"
                          name="phone"
                          type="tel"
                          value={applyForm.phone}
                          onChange={handleChange}
                          required
                          placeholder="123-456-7890"
                          data-testid="apply-input-phone"
                        />
                      </div>
                      <div>
                        <label htmlFor="applyMessage" className="block text-sm font-medium text-[#0B1F3A] mb-2">
                          Tell us about yourself
                        </label>
                        <Textarea
                          id="applyMessage"
                          name="message"
                          value={applyForm.message}
                          onChange={handleChange}
                          placeholder="Share your relevant experience and availability..."
                          rows={3}
                          data-testid="apply-input-message"
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="btn-primary w-full justify-center disabled:opacity-50"
                        data-testid="submit-application"
                      >
                        {isSubmitting ? 'Submitting...' : 'Submit Application'}
                        {!isSubmitting && <ArrowRight size={16} strokeWidth={2} />}
                      </button>
                    </form>
                  </DialogContent>
                </Dialog>
              </motion.div>
            ))}
          </div>

          {filteredJobs.length === 0 && (
            <div className="text-center py-12 text-[#64748B]">
              <p>No jobs found matching your search. Try different keywords.</p>
            </div>
          )}
        </div>
      </section>

      {/* General Apply Section */}
      <section className="section-padding bg-[#0B1F3A]" data-testid="general-apply">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <span className="text-sm text-[#94A3B8] uppercase tracking-[0.2em] font-bold">
                Don't See Your Role?
              </span>
              <h2 className="font-['Chivo'] font-black text-3xl md:text-4xl text-white mt-3 tracking-tight mb-6">
                Submit Your Profile
              </h2>
              <p className="text-[#94A3B8] text-lg leading-relaxed">
                Even if you don't see a perfect match, submit your information and we'll reach out when suitable opportunities arise.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <form 
                onSubmit={(e) => {
                  e.preventDefault();
                  toast.success('Profile submitted!', {
                    description: "We'll contact you when matching opportunities arise."
                  });
                }} 
                className="bg-white p-8"
                data-testid="general-apply-form"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-[#0B1F3A] mb-2">
                      Full Name *
                    </label>
                    <Input
                      type="text"
                      required
                      placeholder="Your name"
                      data-testid="general-input-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0B1F3A] mb-2">
                      Email *
                    </label>
                    <Input
                      type="email"
                      required
                      placeholder="your@email.com"
                      data-testid="general-input-email"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-[#0B1F3A] mb-2">
                    Phone *
                  </label>
                  <Input
                    type="tel"
                    required
                    placeholder="123-456-7890"
                    data-testid="general-input-phone"
                  />
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-[#0B1F3A] mb-2">
                    What type of work are you looking for?
                  </label>
                  <Textarea
                    placeholder="Tell us about your skills, experience, and the type of roles you're interested in..."
                    rows={3}
                    data-testid="general-input-experience"
                  />
                </div>
                <button
                  type="submit"
                  className="btn-primary w-full justify-center"
                  data-testid="submit-general-form"
                >
                  Submit Profile
                  <ArrowRight size={16} strokeWidth={2} />
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
}
